import React from 'react';
import { useTranslation } from 'react-i18next';
import type { SWRConfiguration } from 'swr';
import { mutate } from 'swr';

import { useApi } from 'data/base-api';
import useHandleError from 'hooks/useHandleError';
import useShowAlert from 'hooks/useShowAlert';
import { checkStatus, del, get, post, put } from 'libs/apiClient';
import { latinize } from 'libs/stringFunctions';
import { reduceToObjectById } from 'libs/utils';
import type {
  Company,
  CompanyConfiguration,
  CompanyOption,
  CompanyRelations,
  NewCompany,
  ProjectWithTerminals,
  RelatedCompany,
  TimeZone,
} from 'model/Company/types';

import { uploadFile } from './file-service';

export const companyBaseApiUrl = '/api/v4/companies';

export const useGetRawCompanies = (skip = false, config?: SWRConfiguration) =>
  useApi<Company[]>(skip ? null : companyBaseApiUrl, config);

export const getListOfCompanies = async () => {
  const response = await get(`${companyBaseApiUrl}`).then(checkStatus);

  return response.json();
};

export const searchForNewCompanies = async (
  searchString: string
): Promise<Company[]> => {
  const response = await get(
    `${companyBaseApiUrl}/search/${encodeURIComponent(
      searchString
    )}?excludeProjectCompanies=true`
  ).then(checkStatus);

  return response.json();
};

export const searchForDitioCompanies = async (
  searchString: string
): Promise<Company[]> => {
  if (!searchString.length) {
    return [];
  }

  const response = checkStatus(
    await get(
      `${companyBaseApiUrl}/search/${searchString}?onlyDitioCompanies=true`
    )
  );

  return response.json();
};

export const useGetSearchForDitioCompanies = (searchString: string) => {
  const data = useApi<Company[]>(
    searchString
      ? `${companyBaseApiUrl}/search/${searchString}?onlyDitioCompanies=true`
      : null
  );

  if (!searchString) {
    return {
      mutate: () => searchString,
      isValidating: false,
      data: [] as Company[],
      error: null,
      isFetching: false,
      isEmpty: false,
    };
  }

  return data;
};

export const useGetCompanyById = (companyId: Company['companyId'] | null) =>
  useApi<Company>(companyId ? `${companyBaseApiUrl}/${companyId}` : null);

export const uploadCompanyAvatar = async (
  file: File,
  companyId: Company['companyId'] | null = null
) => {
  const response = await uploadFile({
    file,
    params: companyId
      ? {
          collectionRef: 'Company',
          idRef: companyId,
        }
      : undefined,
    uploadUrl: `${companyBaseApiUrl}/upload-avatar`,
  });

  return response.files[0].fileReference.id;
};

export const putCompanyById = async (company: Company): Promise<Company> => {
  const response = checkStatus(
    await put(`${companyBaseApiUrl}/${company.companyId}`, company)
  );

  const newCompany: Company = await response.json();

  return newCompany;
};

export const createNewCompany = async (
  company: NewCompany
): Promise<Company> => {
  const response = checkStatus(await post(`${companyBaseApiUrl}`, company));

  const newCompany: Company = await response.json();

  return newCompany;
};

export const createNewProjectCompany = async (
  parentCompanyId: Company['companyId'],
  company: NewCompany
): Promise<Company> => {
  const response = checkStatus(
    await post(
      `${companyBaseApiUrl}/${parentCompanyId}/project-company`,
      company
    )
  );

  const newCompany: Company = await response.json();

  return newCompany;
};

export const createNewSubsidiaryCompany = async (
  parentCompanyId: Company['companyId'],
  company: NewCompany
): Promise<Company> => {
  const response = checkStatus(
    await post(
      `${companyBaseApiUrl}/${parentCompanyId}/subsidiary-company`,
      company
    )
  );

  const newCompany: Company = await response.json();

  return newCompany;
};

export const useAddCompanyAsTrustedSubcontractor = () => {
  const handleError = useHandleError();

  return async (parentCompanyId: string, subcontractorCompanyId: string) => {
    await mutate<CompanyRelations>(
      `${companyBaseApiUrl}/${parentCompanyId}/relations`,
      async (data) => {
        try {
          const response = await checkStatus(
            post(
              `${companyBaseApiUrl}/${parentCompanyId}/connect-subcontractor/${subcontractorCompanyId}`
            )
          );

          const parsed: RelatedCompany = await response.json();

          if (parsed) {
            return (
              data && {
                ...data,
                trustedSubcontractors: [...data.trustedSubcontractors, parsed],
              }
            );
          }

          return data;
        } catch (error) {
          handleError(error);

          return data;
        }
      }
    );
  };
};

export const connectExistingCompanyAsSubsidiary = async (
  parentCompanyId: Company['companyId'],
  subsidiaryCompanyId: Company['companyId']
): Promise<void> => {
  await post(`${companyBaseApiUrl}/${parentCompanyId}/subsidiary-company`, {
    companyId: subsidiaryCompanyId,
  }).then(checkStatus);
};

export const getCompanyById = async (
  companyId: Company['companyId']
): Promise<Company> => {
  const response = await get(`${companyBaseApiUrl}/${companyId}`).then(
    checkStatus
  );

  return response.json();
};

export const addCompanyAsTrustedSubcontractor = async (
  parentCompanyId: Company['companyId'],
  subcontractorCompanyId: Company['companyId']
) => {
  const response = await post(
    `${companyBaseApiUrl}/${parentCompanyId}/connect-subcontractor/${subcontractorCompanyId}`
  ).then(checkStatus);

  return response.json();
};

export const deleteCompanyById = async (companyId: Company['companyId']) => {
  const response = await del(`${companyBaseApiUrl}/${companyId}`).then(
    checkStatus
  );

  return response.json();
};

export const useGetCompanyConfiguration = (
  companyId: string | null,
  config?: SWRConfiguration
) =>
  useApi<CompanyConfiguration>(
    companyId ? `${companyBaseApiUrl}/${companyId}/configuration` : null,
    config
  );

export const putCompanyConfiguration = async (
  companyConfiguration: CompanyConfiguration
): Promise<CompanyConfiguration> => {
  const response = checkStatus(
    await put(
      `${companyBaseApiUrl}/${companyConfiguration.companyId}/configuration`,
      {
        ...companyConfiguration,
      }
    )
  );
  const parsed = response.json();

  return parsed;
};

export const useUpdateCompanyConfiguration = () => {
  const handleError = useHandleError();

  return async (companyConfiguration: CompanyConfiguration) => {
    mutate<CompanyConfiguration>(
      `${companyBaseApiUrl}/${companyConfiguration.companyId}/configuration`,
      async (data) => {
        try {
          const response = await putCompanyConfiguration(companyConfiguration);

          return response;
        } catch (error) {
          handleError(error);

          return data;
        }
      },
      {
        revalidate: false,
        optimisticData: companyConfiguration,
      }
    );
  };
};

export const useGetCompanyRelations = (
  companyId?: Company['companyId'] | null
) =>
  useApi<CompanyRelations>(
    companyId ? `${companyBaseApiUrl}/${companyId}/relations` : null
  );

export const searchForCompany = async (
  searchString: string,
  isSubcontractorSearch = false
) => {
  const response = await get(
    `${companyBaseApiUrl}/search/${encodeURIComponent(
      searchString
    )}?onlyBrregCompanies=false&isSubcontractorSearch=${isSubcontractorSearch}`
  );
  const companies: Company[] = await response.json();

  return companies;
};

export const getAvailableTimezones = async (): Promise<TimeZone[]> => {
  const response = await get(`${companyBaseApiUrl}/available-timezones`);
  const timezones = await response.json();

  return timezones;
};

export const useGetAvailableTimezones = () =>
  useApi<TimeZone[]>(`${companyBaseApiUrl}/available-timezones`);

export const useRelatedCompanies = (revalidateOnMount = true) => {
  const { data, ...rest } = useApi<Company[]>(
    `${companyBaseApiUrl}/current/related-companies`,
    { revalidateOnMount }
  );

  const dataWithIdAndName: CompanyOption[] = data?.length
    ? data.map((company) => ({
        ...company,
        id: company.companyId,
        name: company.companyName,
      }))
    : null;

  const dataById = dataWithIdAndName && reduceToObjectById(dataWithIdAndName);

  return {
    data: dataWithIdAndName,
    companiesById: dataById,
    ...rest,
  };
};

export const useGetProjectsWithTerminals = (
  companyId: string | undefined,
  search = '',
  includeWithoutTerminals = false,
  shouldSkip = false
) => {
  const { data, ...rest } = useApi<ProjectWithTerminals[]>(
    shouldSkip || !companyId
      ? null
      : `/api/projects/list-with-terminals/${companyId}?includeWithoutTerminals=${includeWithoutTerminals}`
  );

  const filteredProjects = React.useMemo(() => {
    const searchString = latinize(search).toLowerCase();

    return data?.filter(
      (project) =>
        latinize(project.name).toLowerCase().includes(searchString) ||
        project.number.includes(searchString) ||
        project.terminalIds.includes(searchString)
    );
  }, [data, search]);

  return { projects: filteredProjects, ...rest };
};

export const useUpdateProjectTerminals = () => {
  const { t } = useTranslation();
  const handleError = useHandleError();
  const { showSuccessMessage } = useShowAlert();

  return async (projectId: string, terminalIds: string[]) => {
    try {
      checkStatus(
        await put('/api/projects/terminals', {
          id: projectId,
          terminalIds,
        })
      );
      mutate<ProjectWithTerminals[]>(
        (key) =>
          typeof key === 'string' &&
          key.startsWith('/api/projects/list-with-terminals'),
        (cachedData) => {
          const newData = [...cachedData];

          const updatedProjectIndex = newData.findIndex(
            (project) => project.id === projectId
          );
          newData[updatedProjectIndex].terminalIds = terminalIds;

          return newData;
        }
      );

      showSuccessMessage({
        message: t('Company.TerminalSaveSuccess'),
      });
    } catch (e) {
      handleError(e);
    }
  };
};
